import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { emailAutoLinkSkip, emailAutoLinkWatcher, manualLinkAccount } from "../../redux/actions/loginactioncreators";
import { NewFADVPasswordField } from "../fields/NewFADVPasswordField";

function renderLinkErrorMessage(linkError) {
    if (!linkError) {
        return null;
    }

    if (typeof linkError === "string") {
        return linkError;
    }

    if (linkError.id && linkError.defaultMessage) {
        return <FormattedMessage {...linkError} />;
    }

    return null;
}

function HowItWorksCallout() {
    return (
        <div className="rounded-[10px] bg-accent-100 p-5">
            <h3 className="text-base font-medium leading-5 tracking-tight text-accent-700">
                <FormattedMessage id="signup.autoLink.howItWorksHeader" defaultMessage="How it works" description="Header for the informational callout on the post-signup account-linking page." />
            </h3>
            <p className="mt-1 leading-5 tracking-tight text-primary-text">
                <FormattedMessage id="signup.autoLink.howItWorksBody" defaultMessage="All platforms are linked to your email. Matching credentials are auto-linked; add others with their passwords or skip to continue." description="Body text for the informational callout on the post-signup account-linking page." />
            </p>
        </div>
    );
}

function LinkedBadge({ item }) {
    return (
        <div id={`linked-badge-${item.key}`} className="flex items-center gap-1 text-success-700">
            <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.78-9.72a.75.75 0 10-1.06-1.06L9.25 10.69 7.78 9.22a.75.75 0 00-1.06 1.06l2 2a.75.75 0 001.06 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium leading-5">
                <FormattedMessage id="signup.autoLink.linked" defaultMessage="Linked" description="Status text shown for an account that has already been linked." />
            </span>
        </div>
    );
}

function AccountRow({ item, password, onPasswordChange }) {
    const dispatch = useDispatch();
    const [showMissingPassword, setShowMissingPassword] = useState(false);

    const isLinked = item.linked || item.linkStatus === "linked";
    const hasError = item.linkStatus === "error";
    const isProcessing = item.linkStatus === "linking";
    const containerClassName = isLinked
        ? "border-success-400 bg-success-100 border-2"
        : "border-neutral-300 bg-primary-bg2";

    const handleLinkClick = () => {
        if (!password.trim()) {
            setShowMissingPassword(true);
            return;
        }

        dispatch(manualLinkAccount({
            itemKey: item.key,
            clientId: item.clientId,
            username: item.username,
            password: password.trim(),
            accountNumber: item.accountNumber,
        }));
    };

    const handlePasswordChange = (event) => {
        setShowMissingPassword(false);
        onPasswordChange(event.target.value);
    };

    const showPasswordError = showMissingPassword || hasError;
    let passwordHelperMessage;

    if (showPasswordError) {
        passwordHelperMessage = renderLinkErrorMessage(item.linkError) || (
            <FormattedMessage
                id="signup.autoLink.invalidPassword"
                defaultMessage="Invalid Password."
                description="Fallback helper text shown when account linking fails due to an invalid password."
            />
        );
    } else {
        passwordHelperMessage = (
            <FormattedMessage
                id="signup.autoLink.enterPlatformPassword"
                defaultMessage="Enter your platform password"
                description="Helper text shown under the password field before any password validation error."
            />
        );
    }

    return (
        <div className={twMerge("relative rounded-[10px] border p-4 text-primary-text", containerClassName)}>
            {isLinked ? <div className="absolute right-4 top-4"><LinkedBadge key={item.key} item={item} /></div> : null}
            <div>
                <div className="min-w-0">
                    <h3 className={twMerge("text-lg font-medium leading-6 text-primary-text", isLinked && "pr-24")}>
                        {item.platformName}
                    </h3>

                    <div className="flex justify-between">
                        <p className="text-sm font-normal text-nowrap pr-1">
                            <FormattedMessage
                                id="signup.autoLink.accountNumberLabel"
                                defaultMessage="Client ID:"
                                description="Label for the account identifier fields shown on the post-signup account-linking page."
                            />
                        </p>
                        <p className="text-sm font-medium overflow-hidden">{item.accountNumber}</p>
                    </div>
                    <div className="flex justify-between">
                        <p className="text-sm font-normal text-nowrap pr-1">
                            <FormattedMessage
                                id="signup.autoLink.userIdLabel"
                                defaultMessage="User ID:"
                                description="Label for the account identifier fields shown on the post-signup account-linking page."
                            />
                        </p>
                        <p className="text-sm font-medium overflow-hidden">{item.username}</p>
                    </div>
                    <div className="flex justify-between">
                        <p className="text-sm font-normal text-nowrap pr-1">
                            <FormattedMessage
                                id="signup.autoLink.displayNameLabel"
                                defaultMessage="Name:"
                                description="Label for the account identifier fields shown on the post-signup account-linking page."
                            />
                        </p>
                        <p className="text-sm font-medium overflow-hidden">{item.displayName}</p>
                    </div>

                </div>
            </div>

            {!isLinked ? (
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex-1">
                        <NewFADVPasswordField
                            id={`link-password-${item.key}`}
                            value={password}
                            onChange={handlePasswordChange}
                            className="pb-2 h-[50px]"
                            onBlur={() => {}}
                            label={<FormattedMessage id="signup.autoLink.password" defaultMessage="Password" description="Label for the password input used to link an existing account." />}
                            error={showPasswordError}
                            disabled={isProcessing}
                            wrapperClassName="w-full"
                        />
                        <p className={twMerge("mt-2 flex items-start gap-1 text-sm", showPasswordError ? "text-error-700" : "text-primary-text")}>
                            {showPasswordError ? (
                                <>
                                    <svg aria-hidden="true" className="mt-0.5 h-[15px] w-[15px] flex-none" viewBox="0 0 15 15" fill="none">
                                        <g clipPath="url(#clip0_1276_3639)">
                                            <path d="M6.875 4.375H8.125V5.625H6.875V4.375ZM6.875 6.875H8.125V10.625H6.875V6.875ZM7.5 1.25C4.05 1.25 1.25 4.05 1.25 7.5C1.25 10.95 4.05 13.75 7.5 13.75C10.95 13.75 13.75 10.95 13.75 7.5C13.75 4.05 10.95 1.25 7.5 1.25ZM7.5 12.5C4.74375 12.5 2.5 10.2563 2.5 7.5C2.5 4.74375 4.74375 2.5 7.5 2.5C10.2563 2.5 12.5 4.74375 12.5 7.5C12.5 10.2563 10.2563 12.5 7.5 12.5Z" fill="#D20B5E" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1276_3639">
                                                <rect width="15" height="15" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <span id={`password-error-${item.key}`}>{passwordHelperMessage}</span>
                                </>
                            ) : (
                                <span>{passwordHelperMessage}</span>
                            )}
                        </p>
                    </div>
                    <button
                        id={`link-button-${item.key}`}
                        type="button"
                        disabled={isProcessing}
                        className="h-[50px] rounded-[10px] bg-accent-600 px-6 text-lg font-medium leading-6 text-white transition-colors duration-200 hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
                        onClick={handleLinkClick}
                    >
                        {isProcessing ? (
                            <FormattedMessage id="signup.autoLink.linking" defaultMessage="Linking..." description="Button text shown while an account link is in progress." />
                        ) : (
                            <FormattedMessage id="signup.autoLink.linkAction" defaultMessage="Link Account" description="Button text used to link an account from the post-signup account-linking page." />
                        )}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function VerificationAccountsSection({
    verificationRows,
    passwords,
    updatePassword,
}) {
    if (verificationRows.length === 0) {
        return null;
    }

    return (
        <section className="space-y-3">
            <h3 className="relative top-1 text-black text-base font-medium">
                <FormattedMessage
                    id="signup.autoLink.accountsRequiringVerificationHeader"
                    defaultMessage="Accounts Requiring Verification"
                    description="Header shown above the accounts that still require password verification."
                />
            </h3>
            <div className="space-y-2">
                {verificationRows.map(item => (
                    <AccountRow
                        key={item.key}
                        item={item}
                        password={passwords[item.key] ?? ""}
                        onPasswordChange={(value) => updatePassword(item.key, value)}
                    />
                ))}
            </div>
        </section>
    );
}

export function EmailAutoLinkAccounts() {
    const dispatch = useDispatch();
    const autoLinkItems = useSelector(state => state.loginForm.emailAutoLinkData?.users ?? []);
    const autoLinkError = useSelector(state => state.loginForm.emailAutoLinkData?.error);
    const loginErrorMessages = useSelector(state => state.loginForm.loginErrorMessages ?? []);
    const email = useSelector(state => state.loginForm.email);
    const [passwords, setPasswords] = useState({});

    const introductionEmail = email || autoLinkItems[0]?.email || "";
    const visibleRows = Array.isArray(autoLinkItems) ? autoLinkItems : [];
    const linkedRows = visibleRows.filter(item => item.linked || item.linkStatus === "linked");
    const verificationRows = visibleRows.filter(item => !(item.linked || item.linkStatus === "linked"));
    const globalErrors = useMemo(() => {
        const messages = [];

        if (autoLinkError?.message) {
            messages.push(autoLinkError.message);
        }

        loginErrorMessages.forEach(({ message }) => {
            if (message) {
                messages.push(message);
            }
        });

        return messages;
    }, [autoLinkError, loginErrorMessages]);


    const handleRetry = () => {
        dispatch(emailAutoLinkWatcher());
    };
    const updatePassword = (key, value) => {
        setPasswords(previousState => ({
            ...previousState,
            [key]: value,
        }));
    };

    const handleSkipToAuthenticated = () => {
        dispatch(emailAutoLinkSkip());
    };

    const allItemsLinked = visibleRows.length > 0 && visibleRows.every(item => item.linked || item.linkStatus === "linked");

    const autoLinkStatus = useSelector(state => state.loginForm.emailAutoLinkData?.status);
    const isLoading = autoLinkStatus === "loading" || autoLinkStatus === "needed";
    const showRetryState = autoLinkStatus === "error" || autoLinkError === true;


    if (showRetryState) {
        return (
            <div className="space-y-4">
                <h2 className="mb-2 text-2xl">
                    <FormattedMessage id="login.emailAutoLinkRetryHeader" defaultMessage="We couldn't automatically link your accounts" description="Header shown when the email auto-link attempt fails and the user can retry." />
                </h2>
                <p className="text-base leading-6 text-primary-text">
                        <FormattedMessage id="login.emailAutoLinkRetryBlurb" defaultMessage="Something went wrong linking your accounts for this email, we weren't able to automatically connect your accounts right now." description="Body text for the email auto-link retry page explaining available next actions." />
                </p>
                <p className="text-base leading-6 text-primary-text">
                    <FormattedMessage id="login.emailAutoLinkProceedToDashboardBlurb" defaultMessage="You may alternatively navigate to the Account dashboard and use the 'Add Account' functionality to link your accounts manually." description="Body text for the email auto-link retry page explaining available next actions." />
                </p>

                <div className={twMerge("rounded border border-error-100 bg-error-100 px-5 py-4 text-base text-error-700", globalErrors.length === 0 && "hidden")} role="alert" aria-live={globalErrors.length > 0 ? "assertive" : "off"}>
                    <ul className="list-none space-y-1 pl-0">
                        {globalErrors.map((message, index) => (
                            <li key={`${message}-${index}`}>{message}</li>
                        ))}
                    </ul>
                </div>

                <HowItWorksCallout />

                <button
                    type="button"
                    id="retryEmailAutoLinkButton"
                    className="w-full rounded-[10px] bg-accent-600 px-3.5 py-[9px] text-lg font-medium leading-6 text-white transition-colors duration-200 hover:bg-accent-700"
                    onClick={handleRetry}
                >
                    <FormattedMessage id="login.emailAutoLinkRetryAction" defaultMessage="Retry" description="Primary button text to retry the email auto-link request." />
                </button>
                <button
                    type="button"
                    id="goToDashboardButton"
                    className="w-full rounded-[10px] border border-primary-fg bg-primary-bg2 px-3.5 py-[9px] text-lg font-medium leading-6 transition-colors duration-200 hover:bg-neutral-100"
                    onClick={handleSkipToAuthenticated}
                >
                    <FormattedMessage id="login.emailAutoLinkRetryDashboardAction" defaultMessage="Continue to Dashboard" description="Secondary button text to continue to dashboard instead of retrying email auto-link." />
                </button>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="space-y-4">
                <h2 className="mb-2 text-2xl">
                    <FormattedMessage id="login.emailAutoLinkLoadingHeader" defaultMessage="Linking your accounts" description="Header shown while the email auto-link request is processing." />
                </h2>
                <p className="text-base leading-6 text-primary-text">
                    <FormattedMessage id="login.emailAutoLinkLoadingBlurb" defaultMessage="Please wait while we check for accounts associated with your email address." description="Body text shown while the email auto-link request is processing." />
                </p>
            </div>
        );
    }

    return (
        <div className="text-primary-text">
            <h2 id="emailAutoLinkHeader" className="mb-6 text-2xl">
                <FormattedMessage id="signup.autoLink.header" defaultMessage="Link accounts" description="Header text shown on the post-signup account-linking page." />
            </h2>

            <div className="space-y-4">
                {Boolean(introductionEmail.trim()) &&
                    <p className="inline-flex items-baseline gap-1 text-base font-normal text-primary-fg">
                        <FormattedMessage
                            id="signup.autoLink.userIdWithEmailPrefix"
                            defaultMessage="User ID:"
                            description="Prefix label shown above the account list before the user email for which accounts were found."
                        />
                        <span className="font-medium text-primary-fg">{introductionEmail}</span>
                    </p>
                }
                <p className="text-base leading-6 text-primary-fg">
                    <FormattedMessage
                        id="signup.autoLink.introduction"
                        defaultMessage="We identified multiple platform accounts associated with this user ID. Accounts with matching credentials are linked automatically. Unverified accounts may require password verification."
                        description="Introduction text shown above the list of possible accounts that can be linked after signup."
                    />
                </p>

                <div className={twMerge("rounded border border-error-100 bg-error-100 px-5 py-4 text-base text-error-700", globalErrors.length === 0 && "hidden")} role="alert" aria-live={globalErrors.length > 0 ? "assertive" : "off"}>
                    <ul className="list-none space-y-1 pl-0">
                        {globalErrors.map((message, index) => (
                            <li key={`${message}-${index}`}>{message}</li>
                        ))}
                    </ul>
                </div>

                {linkedRows.length > 0 ? (
                    <section className="space-y-1">
                        <div className="top-1 text-black text-base font-medium">
                            <FormattedMessage
                                id="signup.autoLink.linkedAccountsHeader"
                                defaultMessage="Linked Accounts"
                                description="Header shown above the accounts that are already linked."
                            />
                        </div>
                        <div className="space-y-4">
                            {linkedRows.map(item => (
                                <AccountRow
                                    key={item.key}
                                    item={item}
                                    password={passwords[item.key] ?? ""}
                                    onPasswordChange={(value) => updatePassword(item.key, value)}
                                />
                            ))}
                        </div>
                    </section>
                ) : null}

                <VerificationAccountsSection
                    verificationRows={verificationRows}
                    passwords={passwords}
                    updatePassword={updatePassword}
                />

                <HowItWorksCallout />

                <button
                    id="goToDashboardButton"
                    type="button"
                    className={twMerge(
                        "w-full rounded-[10px] px-3.5 py-[9px] text-lg font-medium leading-6 transition-colors duration-200",
                        allItemsLinked
                            ? "bg-accent-600 text-white hover:bg-accent-700"
                            : "border border-primary-fg bg-primary-bg2 hover:bg-neutral-100"
                    )}
                    onClick={handleSkipToAuthenticated}
                >
                    {allItemsLinked ? (
                        <FormattedMessage id="signup.autoLink.goToDashboard" defaultMessage="Go to Dashboard" description="Button text shown when all accounts are linked and user is ready to proceed." />
                    ) : (
                        <FormattedMessage id="signup.autoLink.skipToDashboard" defaultMessage="Skip to Dashboard" description="Button text for skipping the account-linking step after signup." />
                    )}
                </button>
            </div>
        </div>
    );
}

LinkedBadge.propTypes = {
    item: PropTypes.shape({
        key: PropTypes.string.isRequired,
    }).isRequired,
};

AccountRow.propTypes = {
    item: PropTypes.shape({
        key: PropTypes.string.isRequired,
        clientId: PropTypes.string,
        username: PropTypes.string,
        accountNumber: PropTypes.string,
        platformName: PropTypes.string,
        displayName: PropTypes.string,
        linked: PropTypes.bool,
        linkStatus: PropTypes.string,
        linkError: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                id: PropTypes.string,
                defaultMessage: PropTypes.string,
                description: PropTypes.string,
            }),
        ]),
    }).isRequired,
    password: PropTypes.string.isRequired,
    onPasswordChange: PropTypes.func.isRequired,
};

VerificationAccountsSection.propTypes = {
    verificationRows: PropTypes.array.isRequired,
    passwords: PropTypes.object.isRequired,
    updatePassword: PropTypes.func.isRequired,
};